import { useState, useEffect, useContext } from "src/ReactTS";
import { Auth, Cache } from "aws-amplify";
import { IUser, UserType } from "../interfaces/UserInterface";
import { IAuthInterface } from "../interfaces/IAuthInterface";
import { loginFacebook, facebookLoginStatus, isFacebookAuthenticated, logoutFacebook } from "src/components/common/Facebook/FacebookClient";
import { isGoogleAuthenticated, loginGoogle, logoutGoogle } from "src/components/common/Google/GoogleClient";
import { IsProduction } from "src/services/interfaces/Environment";
import * as AWS from "aws-sdk";
import { ServiceContext } from "../provider/Service.provider";
import ConfigService from '../config/config.service';
import { AdminCreateUserRequest } from 'aws-sdk/clients/cognitoidentityserviceprovider';

export function useAuthProvider(): IAuthInterface {
  const { userAuthService } = useContext(ServiceContext);

  const [currentEmail, setCurrentEmail] = useState<string | null | undefined>(undefined);
  const [currentUser, setCurrentUser] = useState<any>(undefined);
  const [socialEmail, setSocialEmail] = useState<string | null | undefined>(undefined);

  const provider = {
    signUp: async (email: string, password: string) => {
      return Auth.signUp({
        password,
        username: email.toLowerCase(),
        attributes: {
          email: email.toLowerCase()
        }
      });
    },
    confirmSignUp: async (email: string, code: string) => {
      return new Promise<any>(async (res, reject) => {
        try {
          res(await Auth.confirmSignUp(email.toLowerCase(), code));
        } catch (e) {
          if (e.code === "UnexpectedLambdaException" || e.code === "NotAuthorizedException") {
            res(true);
          } else {
            reject(e);
          }
        }
      });
    },
    signIn: async (email: string, password: string) => {
      try {
        Cache.setItem("jwt", null);

        const result = await Auth.signIn(email.toLowerCase(), password);

        if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
          throw result;
        }

        const user = await userAuthService.getPortalUser();

        if (user.active === false) {
          throw new Error("Usuário Inativo");
        }

        setCurrentEmail(email.toLowerCase());
        setCurrentUser(user);

        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function(error) {
          if (error) {
            console.log(error);
          } else {
            console.log("REFRESHED");
          }
        });

        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
        cognitoidentityserviceprovider.config.credentials.secretAccessKey = "";
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    signOut: async () => {
      Cache.setItem("jwt", null);
      if (IsProduction()) {
        const fbStatus = await facebookLoginStatus();
        if (isFacebookAuthenticated(fbStatus)) {
          await logoutFacebook();
        }

        const isGoogleAuth = await isGoogleAuthenticated();
        if (isGoogleAuth) {
          await logoutGoogle();
        }
      }

      return Auth.signOut().then(x => {
        setCurrentUser(null);
        setCurrentEmail(null);
        return x;
      });
    },
    completeNewPassword: async (user: any, newPass: string) => {
      return Auth.completeNewPassword(user, newPass, user.challengeParam.requiredAttributes);
    },
    requestEmailConfirmation: async (email: string) => {
      return Auth.resendSignUp(email.toLowerCase());
    },
    forgotPassword: async (email: string) => {
      return Auth.forgotPassword(email);
    },
    forgotPasswordSubmit: async (email: string, code: string, newPassword: string) => {
      return Auth.forgotPasswordSubmit(email, code, newPassword);
    },
    signInWithFacebook: async () => {
      const res = await loginFacebook();
      setSocialEmail(res.authResponse["email"]);

      let user: IUser = null;
      try {
        user = await userAuthService.getPortalUser();
        setCurrentEmail(user.login);
        setCurrentUser(user);
      } catch (err) {
        console.log(err);
      }

      return user;
    },
    signInWithGoogle: async () => {
      const googleUser = await loginGoogle();
      setSocialEmail(googleUser.getBasicProfile().getEmail());

      let user: IUser = null;
      try {
        user = await userAuthService.getPortalUser();
        setCurrentEmail(user.login);
        setCurrentUser(user);
      } catch (err) {
        console.log(err);
      }

      return user;
    },

    createUserCognito: (email: string, profile: string) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function(error) {
          if (error) {
            console.log(error);
          } else {
            console.log("REFRESHED");
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();
        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
        cognitoidentityserviceprovider.config.credentials.secretAccessKey = "";

        const params = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email,
          UserAttributes: [
            {
              Name: "email",
              Value: email
            },
            {
              Name: "email_verified",
              Value: "true"
            },
            {
              Name: "profile",
              Value: profile === "Administrator" ? "admin" : "officer"
            }
          ]
        };

        cognitoidentityserviceprovider.adminCreateUser(params, function(err, data) {
          if (err) {
            console.log("ERRO createUserCognito", err, err.stack);
          } else {
            console.log("SUCCESS createUserCognito", data);
          }
        });
      });
    },
    resetPasswodCognito: (email: string) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
        cognitoidentityserviceprovider.config.credentials.secretAccessKey = "";

        const paramsUpdate = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email,
          UserAttributes: [
            {
              Name: "email",
              Value: email
            },
            {
              Name: "email_verified",
              Value: "true"
            },
            {
              Name: "profile",
              Value: "app"
            }
          ]
        };

        cognitoidentityserviceprovider.adminUpdateUserAttributes(paramsUpdate, function(errUpdate, dataUpdate) {
          if (errUpdate) {
            console.log("ERRO resetPasswodCognito", errUpdate, errUpdate.stack);
          } else {
            const params: AdminCreateUserRequest = {
              Username: email,
              UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
              MessageAction: "RESEND",
              DesiredDeliveryMediums: ["EMAIL"]
            };
            cognitoidentityserviceprovider.adminCreateUser(params, function(err, data) {
              if (err) {
                console.log("ERRO resetPasswodCognito", err, err.stack);
              } else {
                console.log("SUCCESS resetPasswodCognito", data);
              }
            });
          }
        });
      });
    },
    setPasswordCognito: (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function(error) {
          if (error) {
            console.log(error);
          } else {
            console.log("REFRESHED");
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
        cognitoidentityserviceprovider.config.credentials.secretAccessKey = "";
        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Password: password
        };

        cognitoidentityserviceprovider.adminSetUserPassword(params, function(err, data) {
          if (err) {
            console.log("ERRO resetPasswodCognito", err, err.stack);
          } else {
            console.log("SUCCESS resetPasswodCognito", data);
          }
        });
      });
    },
    getUserCognito: (email: string) => {
      return new Promise<any>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function(error) {
          if (error) {
            console.log(error);
          } else {
            console.log("REFRESHED");
          }
        });

        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
        cognitoidentityserviceprovider.config.credentials.secretAccessKey = "";

        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId
        };

        cognitoidentityserviceprovider.adminGetUser(params, function(err, data) {
          if (err) {
            console.log("ERRO getUserCognito", err, err.stack);
          } else {
            console.log("SUCCESS getUserCognito", data);
          }
        });
      });
    },

    // sendMailToCognito: (email: string) => {
    //   provider
    //     .getUserCognito(email)
    //     .then(() => provider.forgotPassword(email))
    //     .catch(() => provider.signUp(email, "@alterarprimeirasenha"));
    // },

    isLoggedIn: () => !!provider.currentUser,
    isClient: () =>
      !!(
        provider.currentUser &&
        (provider.currentUser.type === UserType.Cliente || provider.currentUser.type === UserType.ClienteMigalhas || provider.currentUser.type === UserType.ClientePan)
      ),

    currentEmail,
    currentUser,
    socialEmail
  };

  useEffect(() => {
    const isAuthenticated = async () => {
      const isProd = IsProduction();
      const currCognitoUser = new Promise<any>(resolve => {
        Auth.currentUserPoolUser()
          .then(resolve)
          .catch(() => resolve(false));
      });
      const isGoogleAuth = isProd ? isGoogleAuthenticated() : Promise.resolve(false);
      const isFacebookAuth = isProd ? facebookLoginStatus() : Promise.resolve({} as fb.StatusResponse);
      const anyLogin = (await currCognitoUser) || (await isGoogleAuth) || isFacebookAuthenticated(await isFacebookAuth);
      if (anyLogin) {
        try {
          const res = await userAuthService.getPortalUser();
          if (res.type === "Cliente" || res.type === "ClienteMigalhas") {
            setMailAndUser(res);
          }
        } catch (error) {
          setMailAndUser(null);
        }
      } else {
        setMailAndUser(null);
      }
    };

    isAuthenticated();
  }, []);

  function setMailAndUser(arg) {
    setCurrentEmail(arg == null ? arg : arg.login);
    setCurrentUser(arg);
  }

  return provider;
}



// WEBPACK FOOTER //
// ./src/services/middleware/AuthProvider.ts